import { createContext, useContext, useReducer, useEffect, useState, useMemo, useCallback } from 'react'; 
import { toast } from 'react-hot-toast';
import { buildImageUrl } from '../utils/imageUtils'; 

const CartContext = createContext({
  items: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  getTotals: () => ({ subtotal: 0, itemCount: 0, tax: 0, shipping: 0, total: 0 }),
  getCartTotals: () => ({ subtotal: 0, itemCount: 0, tax: 0, shipping: 0, total: 0 }),
  isEmpty: () => true,
  isInCart: () => false,
  getItemQuantity: () => 0,
  getItemCount: () => 0,
});

// Cart reducer function
const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { product, quantity = 1, selectedVariant = null } = action.payload;
      
      // Ensure product has a valid ID
      const productId = product._id || product.id;
      if (!productId) {
        console.error('Product missing ID:', product);
        if (typeof window !== 'undefined') toast.error('Erreur: Produit invalide');
        return state;
      }
      
      const itemId = selectedVariant ? `${productId}-${selectedVariant.id}` : productId;

      const existingItemIndex = state.items.findIndex(item => item.id === itemId);
      
      let newItems;
      if (existingItemIndex > -1) {
        newItems = state.items.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
        if (typeof window !== 'undefined') toast.success(`${quantity} autres ${product.name} ajouté au panier`);
      } else {
        const itemToAdd = {
          id: itemId,
          productId: productId,
          name: product.name,
          price: selectedVariant?.price || product.pricing?.price || product.price || 0, 
          image: buildImageUrl(product.images?.[0]?.thumbnail || product.images?.[0]?.url || product.image || '/placeholder.svg'),
          quantity,
          variant: selectedVariant,
          slug: product.slug,
        };
        newItems = [...state.items, itemToAdd];
        if (typeof window !== 'undefined') toast.success(`${product.name} ajouté au panier`);
      }
      return { ...state, items: newItems };
    }
    
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      if (typeof window !== 'undefined') toast.success('Item removed from cart');
      return { ...state, items: newItems };
    }
    
    case 'UPDATE_QUANTITY': {
      const { id: itemId, quantity } = action.payload;
      if (quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(item => item.id !== itemId),
        };
      }
      const newItems = state.items.map(item =>
        item.id === itemId
          ? { ...item, quantity: quantity }
          : item
      );
      return { ...state, items: newItems };
    }
    
    case 'CLEAR_CART': {
      if (typeof window !== 'undefined') toast.success('Cart cleared');
      return { ...state, items: [] };
    }
    
    case 'LOAD_CART': {
      // Filter out any invalid cart items with undefined productIds
      const validItems = action.payload.filter(item => {
        const isValid = item.productId && item.productId !== 'undefined';
        if (!isValid) {
          console.warn('Removing invalid cart item:', item);
        }
        return isValid;
      });
      
      return {
        ...state,
        items: validItems,
      };
    }
    
    default:
      return state;
  }
};

// Initial state for the cart
const initialState = {
  items: [],
};

// Function to initialize state from localStorage
const initCart = (initialValue) => {
  if (typeof window !== 'undefined') {
    try {
      const savedCart = localStorage.getItem('cart');
      // Check for non-empty string (empty string is truthy but invalid JSON)
      if (savedCart && savedCart.trim()) {
        const cartItems = JSON.parse(savedCart);
        // Ensure cartItems is an array
        if (!Array.isArray(cartItems)) {
          console.warn('Invalid cart data format, resetting cart');
          localStorage.removeItem('cart');
          return initialValue;
        }
        // Clean up invalid items before initializing
        const validItems = cartItems.filter(item => {
          return item.productId && item.productId !== 'undefined' && item.name && item.quantity > 0 && item.price >= 0;
        });
        if (validItems.length !== cartItems.length) {
          console.log('Cleaned up invalid cart items during initialization');
          localStorage.setItem('cart', JSON.stringify(validItems));
        }
        return { items: validItems };
      }
    } catch (error) {
      console.error('Error initializing cart from localStorage:', error);
      localStorage.removeItem('cart');
    }
  }
  return initialValue;
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState, initCart);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cart', JSON.stringify(state.items));
    }
  }, [state.items]);

  const addToCart = useCallback((product, quantity = 1, selectedVariant = null) => {
    dispatch({ type: 'ADD_ITEM', payload: { product, quantity, selectedVariant } });
  }, []);

  const removeFromCart = useCallback((itemId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: itemId });
  }, []);

  const updateQuantity = useCallback((itemId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: itemId, quantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const getTotals = useCallback(() => {
    const subtotal = state.items.reduce((total, item) => {
      const itemPrice = Number(item.price) || 0;
      const itemQuantity = Number(item.quantity) || 0;
      return total + (itemPrice * itemQuantity);
    }, 0);
    
    const itemCount = state.items.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
    
    const tax = Math.round(subtotal * 0.05 * 100) / 100; 
    const shipping = Math.round(subtotal * 0.025 * 100) / 100; 
    
    const total = Math.round((subtotal + tax + shipping) * 100) / 100;
    
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      itemCount,
      tax,
      shipping,
      total,
    };
  }, [state.items]);

  const getCartTotals = getTotals;

  const isEmpty = useCallback(() => {
    return state.items.length === 0;
  }, [state.items.length]);

  const isInCart = useCallback((productId, variantId = null) => {
    const itemId = variantId ? `${productId}-${variantId}` : productId;
    return state.items.some(item => item.id === itemId);
  }, [state.items]);

  const getItemQuantity = useCallback((productId, variantId = null) => {
    const itemId = variantId ? `${productId}-${variantId}` : productId;
    const item = state.items.find(item => item.id === itemId);
    return item ? item.quantity : 0;
  }, [state.items]);

  const getItemCount = useCallback(() => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  }, [state.items]);

  const value = useMemo(() => ({
    items: state.items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotals,
    getCartTotals: getTotals, 
    isEmpty,
    isInCart,
    getItemQuantity,
    getItemCount,
  }), [state.items, addToCart, removeFromCart, updateQuantity, clearCart, getTotals, isEmpty, isInCart, getItemQuantity, getItemCount]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  // Handle SSR/SSG where React context might not be available
  if (typeof window === 'undefined') {
    return {
      items: [],
      addToCart: () => {},
      removeFromCart: () => {},
      updateQuantity: () => {},
      clearCart: () => {},
      getTotals: () => ({ subtotal: 0, itemCount: 0, tax: 0, shipping: 0, total: 0 }),
      getCartTotals: () => ({ subtotal: 0, itemCount: 0, tax: 0, shipping: 0, total: 0 }),
      isEmpty: () => true,
      isInCart: () => false,
      getItemQuantity: () => 0,
      getItemCount: () => 0,
    };
  }

  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
